import {
  DataTypeStore,
  MappedResponseData,
  LayoutResponseType,
  getLayoutByIdApi,
  showLoader,
  addToaster,
  hideLoader,
  resetMaxRteDataReducer,
  createSubResource,
  MAX_RTE_CONSTANTS,
} from '@app-store'
import {
  MODE_CONSTANTS,
  Modal,
  Loader,
  SvgCross,
  CustomButton,
  Accordion,
  Tooltip,
  columnTypesConst,
  CustomTable,
  Pagination,
  SORT_KEYS,
  TableColumnType,
  SvgWarningNew,
  OnColumnClickCallbackType,
  useDidMountEffect,
} from '@ui-kit'
import { cloneDeep, isEmpty, omit } from 'lodash'
import moment from 'moment'
import { useState, useEffect, forwardRef } from 'react'
import { FieldValues } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import {
  ROWS_TAB_DIFFERENCE_FOR_LAYOUT_RENDER_TABS,
  ShortcutKeys,
  TAB_INDEX_DEFAULT,
} from '../../constants/form.constants'
import { TabIndexMap } from '../../types/common'
import { EntityLayout, LayoutData, Page } from '../../types/layout.types'
import {
  ListingTableProps,
  ListingTableRow,
} from '../../types/listingTable.types'
import {
  FormHeaderForMaxRTEProps,
  MaxRTELayoutConfig,
  TargetRefMaxRteType,
} from '../../types/maxRTE.types'
import { useAppSelector, RootState } from '../../utils/agent.store'
import {
  ENTITY_NAME,
  FIELD_VALUE_CONSTANTS,
  NAMESPACES,
} from '../../utils/constants'
import { MAX_RTE_STRINGS, STRINGS } from '../../utils/strings'
import {
  generateTabIndexMap,
  getMaxTabIndexValue,
} from '../../utils/tab-indexing'
import FormRenderer from '../FormRenderer'
import { v4 as uuid } from 'uuid'
import { AddDataModal } from '../MultiLookupRowRender'
import {
  CriteriaType,
  fetchInitialData,
  MAX_RTE_NAME_SPACE,
} from '../../utils/maxRTE'
import { useParams, useSearchParams } from 'react-router-dom'
import { getEntityDataByIdEmbedded } from '../../apis/entityData.api'
import PopoutableListingShell, {
  ListingPopoutExpandButton,
} from '../listing-table/PopoutableListingShell'

const FormHeaderForMaxRTE = forwardRef<
  TargetRefMaxRteType,
  FormHeaderForMaxRTEProps
>(({ mode, layout, dataId }, ref) => {
  const dispatch = useDispatch()
  const { responseData: maxRteData } = useAppSelector(
    (state: RootState) => state.maxRte
  )
  const [patientId, setPatientId] = useState<number>()
  const categoryMap = useAppSelector(
    (state: RootState) => state.categoryDropdown?.categoryMap
  )
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const { caseId, id } = useParams()
  const [searchParams] = useSearchParams()
  const queryParamCaseId = searchParams.get('caseId')

  const [dataValue, setDataValue] = useState<DataTypeStore | null>(null)
  const [tabIndexMapForAddModal, setTabIndexMapForAddModal] =
    useState<TabIndexMap>({})
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false)
  const [tableMaxRteData, setTableMaxRteData] = useState<
    MappedResponseData[] | null
  >(null)
  const maxRteConfigInitialState: MaxRTELayoutConfig = {
    layoutId: null,
    entityId: null,
    relationIdentifier: null,
    entityName: null,
    parentEntityId: null,
    parentEntityName: null,
    parentLayoutId: null,
    createLayoutId: null,
  }
  const [maxRteConfig, setmaxRteConfig] = useState<MaxRTELayoutConfig>(
    maxRteConfigInitialState
  )
  const [editLayout, setEditLayout] = useState<LayoutResponseType>()
  const fetchEditLayout = async () => {
    try {
      setIsLoading(true)
      const keyName = ENTITY_NAME.C_PATIENT_INSURANCES
      const entityId =
        layout?.added_fields?.[
          ENTITY_NAME.C_PATIENT_INSURANCES ?? 0
        ]?.relationship?.target_entity_id.toString() ?? ''

      const layoutId =
        layout?.added_fields?.[keyName ?? 0]?.create_layout_id ?? ''
      const response = await getLayoutByIdApi({ entityId, layoutId })
      setEditLayout(response?.data)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }
  const toggleAddModal = () => {
    setIsAddModalOpen((prev) => !prev)

    if (!isAddModalOpen) {
      fetchEditLayout()
    }
  }
  const replicateToInsurance = async (row: DataTypeStore) => {
    try {
      let data: FieldValues = omit(row, 'id')
      if (data.effective_start_date) {
        data.effective_date = moment(
          data.eligibility_start_date as string
        ).format('YYYY-MM-DD')
      }

      if (data.eligibility_end_date) {
        data.expiration_date = moment(
          data.eligibility_end_date as string
        ).format('YYYY-MM-DD')
      }
      if (data.policy_rank_id) {
        data.policy_rank = {
          id: categoryMap?.['Payer Rank']?.filter(
            (val) => val?.value === data.policy_rank_id
          )?.[0]?.id,
        }
      }
      if (data.policy_status) {
        data.policy_status = {
          id: categoryMap?.['Policy Status']?.filter(
            (val) => val?.value === data.policy_status
          )?.[0]?.id,
        }
      }
      if (data.policy_source_id) {
        data.policy_source = {
          id: categoryMap?.['Policy Source']?.filter(
            (val) => val?.value === data.policy_source_id
          )?.[0]?.id,
        }
      }
      data = { ...data, payer: null, plan: null }
      dispatch(showLoader())
      setDataValue(data)
      toggleAddModal()
    } catch (err) {
      console.error(err)
      dispatch(
        addToaster({
          id: uuid(),
          message: MAX_RTE_STRINGS.REPLICATE_TO_INSURANCE_ERR,
          type: 'error',
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }

  const getActions = (): ListingTableProps['extraActions'] =>
    mode === STRINGS.NEW_EDIT
      ? [
          {
            label: MAX_RTE_NAME_SPACE.COPY,
            key: MAX_RTE_NAME_SPACE.COPY,
            renderActionComponent: (row) => {
              if (Array.isArray(row.errors) && row?.errors?.length) return null
              return (
                <Tooltip content={MAX_RTE_NAME_SPACE.COPY}>
                  <button
                    type="button"
                    className="font-primary font-light text-sm rounded truncate border
      px-[0.625rem] w-fit
        cursor-pointer
      border-primary-500 text-accent-900
      "
                    onClick={() => {
                      replicateToInsurance(row)
                    }}
                  >
                    Copy
                  </button>
                </Tooltip>
              )
            },
          },
        ]
      : []

  const handleSubmit = async (values: FieldValues) => {
    try {
      dispatch(showLoader())
      const payload = {
        policy_status: values['policy_status'],
        payer: values['payer'],
        plan: values['plan'],
        pbm_name: values['pbm_name'],
        group_id: values['group_id'],
        subscriber_first_name: values['subscriber_first_name'],
        subscriber_employer: values['subscriber_employer'],
        policy_rank: values['policy_rank'],
        phone_pbm: values['phone_pbm'],
        rx_bin: values['rx_bin'],
        subscriber_dob: values['subscriber_dob'],
        subscriber_last_name: values['subscriber_last_name'],
        effective_date: values['effective_date'],
        policy_source: values['policy_source'],
        plan_phone: values['plan_phone'],
        rx_pcn: values['rx_pcn'],
        expiration_date: values['expiration_date'],
        policy_id: values['policy_id'],
        rx_group: values['rx_group'],
        benefits_list: values['benefits_list'],
      }

      const response = await createSubResource(
        maxRteConfig.entityName ?? '',
        maxRteConfig.entityId?.toString() ?? '',
        maxRteConfig.createLayoutId ?? '',
        dataId,
        maxRteConfig?.relationIdentifier ?? '',
        payload
      )
      if (response) {
        const myEvent = new CustomEvent('maxRteEvent')
        document.dispatchEvent(myEvent)
        dispatch(
          addToaster({
            id: uuid(),
            message: MAX_RTE_STRINGS.APPROVE_TOASTER_MSG,
            type: 'success',
          })
        )
        setIsAddModalOpen(false)
      } else {
        dispatch(
          addToaster({
            id: uuid(),
            message: MAX_RTE_STRINGS.REPLICATE_TO_INSURANCE_ERR,
            type: 'error',
          })
        )
      }
    } catch (e) {
      const err = e as {
        response?: {
          data?: {
            message?: string
          }
        }
        message?: string
      }
      const responseErrorMessage = err?.response?.data?.message
      const errorMessage = responseErrorMessage?.split('\n')[0]
      dispatch(
        addToaster({
          id: uuid(),
          message: errorMessage ?? MAX_RTE_STRINGS.REPLICATE_TO_INSURANCE_ERR,
          type: 'error',
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }
  const fetchPatientIdFromCase = async (caseIdParam: string) => {
    try {
      // Replace with your actual API call to get patient ID from case
      const response = await getEntityDataByIdEmbedded(
        workflowDefinition?.global_variables?.entity_name ?? '',
        Number(workflowDefinition?.global_variables?.entity_id),
        ['patient'],
        Number(caseIdParam)
      )
      const fetchedPatientId = response?.data?.patient?.id
      return fetchedPatientId
    } catch (err) {
      console.error('Error fetching patient ID from case:', err)
      return null
    }
  }
  useEffect(() => {
    if (layout) {
      const keyName = layout.added_fields?.[ENTITY_NAME.C_PATIENT_INSURANCES]
      const layoutId = keyName?.list_view_layout_id ?? ''
      const entityId = (
        keyName?.relationship?.target_entity_id ?? 0
      )?.toString()
      const entityName = keyName?.relationship?.target_entity ?? ''
      const relationIdentifier =
        keyName?.relationship?.relation_identifier ?? ''
      const parentEntityId = (
        keyName?.relationship?.parent_entity_id ?? 0
      )?.toString()
      const parentEntityName = keyName?.relationship?.parent_entity ?? ''
      const parentLayoutId = (keyName?.id ?? 0)?.toString()
      const createLayoutId = keyName?.create_layout_id ?? ''
      setmaxRteConfig({
        layoutId,
        entityId,
        relationIdentifier,
        entityName: entityName,
        parentEntityId,
        parentEntityName,
        parentLayoutId,
        createLayoutId,
      })
    }
  }, [layout])

  useEffect(() => {
    if (isAddModalOpen && editLayout?.layout_data) {
      setTabIndexMapForAddModal(
        generateTabIndexMap(
          (editLayout?.layout_data?.pages as unknown as Page[]) ?? [],
          ROWS_TAB_DIFFERENCE_FOR_LAYOUT_RENDER_TABS
        )
      )
    }
  }, [editLayout?.layout_data, isAddModalOpen])

  useEffect(() => {
    if (maxRteData) setTableMaxRteData(cloneDeep(maxRteData))
  }, [maxRteData])

  useEffect(() => {
    const initializePatientId = async () => {
      if (!layout) return
      const validCaseId = caseId ?? queryParamCaseId
      let resolvedPatientId = Number(dataId)

      if (!dataId && validCaseId) {
        // Workflow page - fetch patient ID from case
        resolvedPatientId = await fetchPatientIdFromCase(validCaseId)
      }
      // Fetch initial data once patient ID is resolved
      if (resolvedPatientId && layout) {
        setPatientId(resolvedPatientId)
        if (
          maxRteData === null ||
          (Array.isArray(maxRteData) && !maxRteData?.length)
        )
          await fetchInitialData(resolvedPatientId)
      }
    }
    if (!patientId) {
      initializePatientId()
    }
    return () => {
      dispatch(resetMaxRteDataReducer())
    }
  }, [caseId, id, layout, dataId])

  if (mode === MODE_CONSTANTS.EDIT || mode === MODE_CONSTANTS.VIEW)
    return (
      <div ref={ref} className="mb-2">
        <MaxRteAccordion
          patientId={patientId}
          tableData={tableMaxRteData ?? []}
          extraActions={getActions()}
          isAddModalOpen={isAddModalOpen}
        />
        {isAddModalOpen && dataValue && (
          <AddDataModal>
            <Modal
              isOpen={isAddModalOpen}
              closeModal={toggleAddModal}
              showCloseButton={false}
              width="min-w-[75vw] max-w-[70%]"
              isDragable={true}
              isResizable
            >
              {isLoading ? (
                <Loader isGlobal={false} loaderHeight="40" loaderWidth="40" />
              ) : (
                <div className="">
                  {editLayout ? (
                    <FormRenderer
                      heading={
                        <div className="flex items-center justify-between gap-4">
                          <span className="font-medium text-base">
                            {
                              MAX_RTE_NAME_SPACE.ADD_INSURANCE_POLICY_INFORMATION
                            }
                          </span>
                          <div className="flex items-center gap-3">
                            <div className="cursor-pointer">
                              <SvgCross
                                onClick={toggleAddModal}
                                tabIndex={
                                  ROWS_TAB_DIFFERENCE_FOR_LAYOUT_RENDER_TABS
                                }
                                onKeyDown={(e) => {
                                  if (
                                    e?.key === ShortcutKeys.Enter ||
                                    e?.key === ShortcutKeys.Space
                                  ) {
                                    toggleAddModal()
                                    e?.preventDefault()
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      }
                      fullLayout={editLayout as EntityLayout}
                      layout={editLayout?.layout_data as LayoutData}
                      mode={'add'}
                      data={dataValue}
                      handleFormSubmit={handleSubmit}
                      footer={
                        <div className="flex items-center justify-end gap-4 mt-4">
                          <div className="flex items-center gap-3">
                            <div className="flex gap-3">
                              <CustomButton
                                onClick={() => {
                                  setIsAddModalOpen(false)
                                }}
                                tabIndex={getMaxTabIndexValue({
                                  tabIndexMapForAddModal,
                                })}
                              >
                                <span>{NAMESPACES.CANCEL_BUTTON}</span>
                              </CustomButton>
                              <CustomButton
                                isFilled
                                type="submit"
                                tabIndex={
                                  getMaxTabIndexValue({
                                    tabIndexMapForAddModal,
                                  }) + 1
                                }
                              >
                                <span>{NAMESPACES.SAVE_BUTTON}</span>
                              </CustomButton>
                            </div>
                          </div>
                        </div>
                      }
                      tabIndexMap={tabIndexMapForAddModal}
                      // addTaskId={addTaskId}
                    />
                  ) : null}
                </div>
              )}
            </Modal>
          </AddDataModal>
        )}
      </div>
    )
})

FormHeaderForMaxRTE.displayName = 'FormHeaderForMaxRTE'

const DateTimeFormatter = (utcString: string) => {
  return moment.utc(utcString).format('MM/DD/YYYY hh:mm A')
}

const DateFormatter = (utcString: string) => {
  return moment.utc(utcString).format('MM/DD/YYYY')
}

interface ErrorsType {
  message: string
  errorsArr: string[]
  traceId: string
}

const SuccessAccordion = ({
  // listingLayout,
  data,
  extraActions,
  patientId,
  isAddModalOpen,
}: // maxRteConfig,
{
  data?: ListingTableRow[]
  extraActions: ListingTableProps['extraActions']
  patientId?: number
  isAddModalOpen?: boolean
}) => {
  const { resMetaData } = useAppSelector((state: RootState) => state.maxRte)
  const [showModal, setShowModal] = useState(false)
  const [errors, setErrors] = useState<ErrorsType | null>(null)
  const [isListingPopoutOpen, setIsListingPopoutOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState<number>(
    FIELD_VALUE_CONSTANTS.ONE
  )
  const [rowPerPage, setRowPerPage] = useState<number>(
    resMetaData?.size ?? FIELD_VALUE_CONSTANTS.TWENTY_FIVE
  )
  const [sortDirection, setSortDirection] = useState<{
    keyName: string
    direction: SORT_KEYS
    isAsc: SORT_KEYS
    key: string
  }>({
    key: MAX_RTE_CONSTANTS.KEYS.MAXTE_REQUEST_DATE_TIME,
    isAsc: SORT_KEYS.Desc,
    keyName: MAX_RTE_CONSTANTS.KEYS.MAXTE_REQUEST_DATE_TIME,
    direction: SORT_KEYS.Desc,
  })

  const columns: TableColumnType<DataTypeStore>[] = [
    {
      header: MAX_RTE_CONSTANTS.LABELS.MAXTE_REQUEST_DATE_TIME,
      accessor: MAX_RTE_CONSTANTS.KEYS.MAXTE_REQUEST_DATE_TIME,
      type: columnTypesConst.COMPONENT,
      isSortable: true,
      renderComponent: (
        row: DataTypeStore & {
          [K in (typeof MAX_RTE_CONSTANTS.KEYS)[keyof typeof MAX_RTE_CONSTANTS.KEYS]]?: string
        }
      ) => {
        return (
          <span>
            {row[MAX_RTE_CONSTANTS.KEYS.MAXTE_REQUEST_DATE_TIME]
              ? DateTimeFormatter(
                  row[MAX_RTE_CONSTANTS.KEYS.MAXTE_REQUEST_DATE_TIME] as string
                )
              : '--'}
          </span>
        )
      },
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.ERROR_MESSAGE,
      accessor: MAX_RTE_CONSTANTS.KEYS.ERROR_MESSAGE,
      type: columnTypesConst?.HYPERLINK,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.POLICY_STATUS,
      accessor: MAX_RTE_CONSTANTS.KEYS.POLICY_STATUS,
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.POLICY_RANK,
      accessor: MAX_RTE_CONSTANTS.KEYS.POLICY_RANK,
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.POLICY_SOURCE,
      accessor: MAX_RTE_CONSTANTS.KEYS.POLICY_SOURCE,
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.PAYER_NAME,
      accessor: MAX_RTE_CONSTANTS.KEYS.PAYER_NAME,
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.PAYER_TYPE,
      accessor: MAX_RTE_CONSTANTS.KEYS.PAYER_TYPE,
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.PLAN_NAME,
      accessor: MAX_RTE_CONSTANTS.KEYS.PLAN_NAME,
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.POLICY_ID,
      accessor: MAX_RTE_CONSTANTS.KEYS.POLICY_ID,
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.GROUP_ID,
      accessor: MAX_RTE_CONSTANTS.KEYS.GROUP_ID,
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.ELIGIBILITY_START_DATE,
      accessor: MAX_RTE_CONSTANTS.KEYS.ELIGIBILITY_START_DATE,
      type: columnTypesConst.COMPONENT,
      dataType: 'string',
      isSortable: true,
      renderComponent: (
        row: DataTypeStore & {
          [K in (typeof MAX_RTE_CONSTANTS.KEYS)[keyof typeof MAX_RTE_CONSTANTS.KEYS]]?: string
        }
      ) => {
        return (
          <span>
            {row[MAX_RTE_CONSTANTS.KEYS.ELIGIBILITY_START_DATE]
              ? DateFormatter(
                  row[MAX_RTE_CONSTANTS.KEYS.ELIGIBILITY_START_DATE] as string
                )
              : '--'}
          </span>
        )
      },
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.ELIGIBILITY_END_DATE,
      accessor: MAX_RTE_CONSTANTS.KEYS.ELIGIBILITY_END_DATE,
      type: columnTypesConst.COMPONENT,
      dataType: 'string',
      isSortable: true,
      renderComponent: (
        row: DataTypeStore & {
          [K in (typeof MAX_RTE_CONSTANTS.KEYS)[keyof typeof MAX_RTE_CONSTANTS.KEYS]]?: string
        }
      ) => {
        return (
          <span>
            {row[MAX_RTE_CONSTANTS.KEYS.ELIGIBILITY_END_DATE]
              ? DateFormatter(
                  row[MAX_RTE_CONSTANTS.KEYS.ELIGIBILITY_END_DATE] as string
                )
              : '--'}
          </span>
        )
      },
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.PBM_NAME,
      accessor: MAX_RTE_CONSTANTS.KEYS.PBM_NAME,
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.PBM_PHONE,
      accessor: 'name',
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.RX_BIN,
      accessor: MAX_RTE_CONSTANTS.KEYS.RX_BIN,
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.RX_PCN,
      accessor: MAX_RTE_CONSTANTS.KEYS.RX_PCN,
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.RX_GROUP,
      accessor: MAX_RTE_CONSTANTS.KEYS.RX_GROUP,
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.BENEFITS_LIST,
      accessor: MAX_RTE_CONSTANTS.KEYS.BENEFITS_LIST,
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.EFFECTIVE_START_DATE,
      accessor: MAX_RTE_CONSTANTS.KEYS.EFFECTIVE_START_DATE,
      type: columnTypesConst.COMPONENT,
      dataType: 'string',
      isSortable: true,
      renderComponent: (
        row: DataTypeStore & {
          [K in (typeof MAX_RTE_CONSTANTS.KEYS)[keyof typeof MAX_RTE_CONSTANTS.KEYS]]?: string
        }
      ) => {
        return (
          <span>
            {row[MAX_RTE_CONSTANTS.KEYS.EFFECTIVE_START_DATE]
              ? DateFormatter(
                  row[MAX_RTE_CONSTANTS.KEYS.EFFECTIVE_START_DATE] as string
                )
              : '--'}
          </span>
        )
      },
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.EFFECTIVE_END_DATE,
      accessor: MAX_RTE_CONSTANTS.KEYS.EFFECTIVE_END_DATE,
      type: columnTypesConst.COMPONENT,
      dataType: 'string',
      isSortable: true,
      renderComponent: (row: DataTypeStore) => {
        return (
          <span>
            {row[MAX_RTE_CONSTANTS.KEYS.EFFECTIVE_END_DATE]
              ? DateFormatter(
                  row[MAX_RTE_CONSTANTS.KEYS.EFFECTIVE_END_DATE] as string
                )
              : '--'}
          </span>
        )
      },
    },
    {
      header: MAX_RTE_CONSTANTS.LABELS.EXTERNAL_TRACE_ID,
      accessor: MAX_RTE_CONSTANTS.KEYS.EXTERNAL_TRACE_ID,
      type: columnTypesConst?.TEXT,
      dataType: 'string',
      isSortable: true,
    },
  ]

  const onSortClick = (key: string, direction: SORT_KEYS) => {
    setSortDirection({
      key: key,
      isAsc: direction,
      keyName: key,
      direction: direction,
    })
  }

  useDidMountEffect(() => {
    const payload: CriteriaType = {
      filterCriteria: [
        ...(sortDirection.direction !== SORT_KEYS.Disabled
          ? [
              {
                direction: sortDirection.direction.toUpperCase(),
                field_name: sortDirection.keyName,
                operation: 'LIKE',
                values: [],
              },
            ]
          : []),
      ],
      page: currentPage - 1,
      size: rowPerPage,
    }
    fetchInitialData(Number(patientId), payload)
  }, [sortDirection, currentPage, rowPerPage])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleRowChange = (row: number) => {
    setRowPerPage(row)
    setCurrentPage(FIELD_VALUE_CONSTANTS.ONE)
  }

  const onErrorMessageClick = (
    message: string,
    errorsArr: string[],
    traceId: string
  ) => {
    if (message) {
      setErrors({ message, errorsArr, traceId })
      setShowModal(true)
    }
  }

  const onColumnClick: OnColumnClickCallbackType<DataTypeStore> = (
    isHeader: boolean,
    accessor: string | number,
    row: DataTypeStore | null | undefined
  ) => {
    if (isHeader || !row) return
    switch (accessor) {
      case MAX_RTE_CONSTANTS.KEYS.ERROR_MESSAGE:
        onErrorMessageClick(
          row[MAX_RTE_CONSTANTS.KEYS.ERROR_MESSAGE] as string,
          row[MAX_RTE_CONSTANTS.KEYS.ERRORS] as string[],
          row[MAX_RTE_CONSTANTS.KEYS.EXTERNAL_TRACE_ID] as string
        )
        break
    }
  }

  const getAccordionHeading = (label: string) => {
    return (
      <div className="flex items-center justify-between">
        <div className="flex gap-3 justify-between">
          <span className="font-primary">{label}</span>
        </div>
        <div className="flex gap-2 items-center">
          <ListingPopoutExpandButton
            onClick={() => {
              setIsListingPopoutOpen(true)
            }}
            disabled={isListingPopoutOpen}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="mt-2">
      <Accordion
        heading={getAccordionHeading(NAMESPACES.PATIENT_EINSURANCE_DETAILS)}
        isDefaultActive={true}
        tabIndex={TAB_INDEX_DEFAULT}
      >
        <PopoutableListingShell
          isOpen={isListingPopoutOpen}
          onClose={() => setIsListingPopoutOpen(false)}
          title={NAMESPACES.PATIENT_EINSURANCE_DETAILS}
          disableFocusTrap={isAddModalOpen}
        >
          <div className="h-full flex flex-col min-h-0">
            <CustomTable
              tableName="max-rte"
              actions={extraActions}
              data={data as DataTypeStore[]}
              columns={columns}
              onSortClick={onSortClick}
              disabledKey="disabled"
              showTableColumnOnEmpty
              sort={
                sortDirection.key && !isEmpty(sortDirection?.key)
                  ? { key: sortDirection?.key, isAsc: sortDirection?.isAsc }
                  : null
              }
              emptyDataMessage={NAMESPACES.EMPTY_MAXRTE_DATA}
              onColumnClick={onColumnClick}
              key={`entity-listing-table`}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={resMetaData?.totalPages ?? 0}
              rowsPer={rowPerPage}
              onPageChange={handlePageChange}
              onRowsChange={handleRowChange}
              numberOfElements={resMetaData?.totalNumberOfElements}
            />
          </div>
        </PopoutableListingShell>
      </Accordion>
      <AddDataModal>
        <Modal
          isOpen={showModal}
          closeModal={() => setShowModal(false)}
          showCloseButton={true}
          width="min-w-[30vw] max-w-[70%]"
        >
          <div className="flex flex-col gap-2 p-2">
            <div className="flex justify-center">
              <div className="flex items-center gap-2">
                <SvgWarningNew />
                <div>
                  <p>{errors?.message}</p>
                  <p>{`Trace ID : ${errors?.traceId}`}</p>
                </div>
              </div>
            </div>
            <p>Error Details</p>
            <ul>
              {errors?.['errorsArr']?.map((msg) => {
                return <li className="list-disc list-inside">{`${msg}`}</li>
              })}
            </ul>
          </div>
        </Modal>
      </AddDataModal>
    </div>
  )
}

const MaxRteAccordion = ({
  tableData,
  extraActions,
  patientId,
  isAddModalOpen,
}: {
  tableData?: MappedResponseData[]
  extraActions: ListingTableProps['extraActions']
  patientId?: number
  isAddModalOpen?: boolean
}) => {
  return (
    <div className="my-2">
      <SuccessAccordion
        data={tableData as unknown as ListingTableRow[]}
        extraActions={extraActions}
        patientId={patientId}
        isAddModalOpen={isAddModalOpen}
      />
    </div>
  )
}

export default FormHeaderForMaxRTE
